// Minimal, framework-free replacement for Bootstrap's modal component, only
// covering what picture-editor-short.ejs's [data-bs-toggle="modal"] /
// picture-big.ejs's [data-bs-dismiss="modal"] pattern actually needs: open
// one .modal by id, dim the page behind it, close on backdrop click / Esc /
// the close button. Domains that dropped the real Bootstrap import (see
// _bootstrap-lite.scss for the matching CSS) still use this exact markup
// for their picture lightboxes, so the trigger/target attributes are kept
// as-is rather than inventing a new data-* convention.
export class Lightbox {
    private openModal: HTMLElement | null = null;
    private backdrop: HTMLElement | null = null;

    public constructor(root: ParentNode = document) {
        root.querySelectorAll<HTMLElement>('[data-bs-toggle="modal"]').forEach((trigger) => {
            trigger.addEventListener('click', (event) => {
                const targetSelector = trigger.getAttribute('data-bs-target');
                if (!targetSelector) return;

                const modal = document.querySelector<HTMLElement>(targetSelector);
                if (!modal) return;

                event.preventDefault();
                this.open(modal);
            });
        });

        root.querySelectorAll<HTMLElement>('[data-bs-dismiss="modal"]').forEach((dismissButton) => {
            dismissButton.addEventListener('click', () => {
                const modal = dismissButton.closest<HTMLElement>('.modal');
                if (modal) this.close(modal);
            });
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.openModal) {
                this.close(this.openModal);
            }
        });
    }

    private open(modal: HTMLElement): void {
        if (this.openModal === modal) return;
        if (this.openModal) this.close(this.openModal);

        modal.classList.add('show');
        modal.setAttribute('aria-modal', 'true');
        modal.removeAttribute('aria-hidden');
        document.body.classList.add('modal-open');

        const backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop show';
        backdrop.addEventListener('click', () => this.close(modal));
        document.body.appendChild(backdrop);

        this.openModal = modal;
        this.backdrop = backdrop;
    }

    private close(modal: HTMLElement): void {
        modal.classList.remove('show');
        modal.setAttribute('aria-hidden', 'true');
        modal.removeAttribute('aria-modal');
        document.body.classList.remove('modal-open');

        this.backdrop?.remove();
        this.backdrop = null;

        if (this.openModal === modal) this.openModal = null;
    }
}
